<template>
  <el-dialog
    :title="isEdit ? '编辑配置' : '新增配置'"
    :visible.sync="dialogVisible"
    width="820px"
    custom-class="welcome-config-dialog"
    @close="handleClose"
  >
    <el-form :model="form" label-width="120px" ref="formRef">
      <el-form-item label="门店ID" prop="storeId" :rules="[{ required: true, message: '请输入门店编号', trigger: 'blur' }]">
        <el-input v-model="form.storeId" placeholder="输入门店编号"></el-input>
      </el-form-item>
      <el-form-item label="门店名称" prop="storeName" :rules="[{ required: true, message: '请输入门店名称', trigger: 'blur' }]">
        <el-input v-model="form.storeName" placeholder="输入门店名称"></el-input>
      </el-form-item>
      <el-form-item label="欢迎语内容" prop="welcomeText" :rules="[{ required: true, message: '请输入欢迎语', trigger: 'blur' }]">
        <el-input v-model="form.welcomeText" type="textarea" placeholder="输入欢迎语"></el-input>
      </el-form-item>
      
      <!-- 文字样式配置区域 -->
      <el-divider content-position="left">文字样式设置</el-divider>
      
      <el-form-item label="文字大小">
        <el-select v-model="form.fontSize" placeholder="选择文字大小">
          <el-option label="默认 (16px)" :value="16"></el-option>
          <el-option label="中号 (20px)" :value="20"></el-option>
          <el-option label="大号 (36px)" :value="36"></el-option>
          <el-option label="特大 (50px)" :value="50"></el-option>
          <el-option label="超大 (65px)" :value="65"></el-option>
          <el-option label="最大 (120px)" :value="120"></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="文字颜色">
        <el-color-picker v-model="form.textColor" show-alpha :predefine="predefineColors"></el-color-picker>
        <span style="margin-left: 10px; color: #666;">当前颜色: {{ form.textColor || '#000000' }}</span>
      </el-form-item>
      
      <el-form-item label="展示方式">
        <el-radio-group v-model="form.isFullscreen">
          <el-radio :label="0">半屏展示</el-radio>
          <el-radio :label="1">全屏展示</el-radio>
        </el-radio-group>
        <div style="margin-top: 8px; color: #909399; font-size: 12px;">
          <div>半屏展示：欢迎语显示在屏幕上半部分，与患者模块同时显示</div>
          <div>全屏展示：欢迎语作为独立页面，在患者模块轮播完成后展示15秒</div>
        </div>
      </el-form-item>
      
      <el-form-item label="样式预览">
        <div class="style-preview" :style="previewStyle" :class="{ 'preview-fullscreen': form.isFullscreen === 1 }">
          <!-- 半屏模式：滚动显示 -->
          <template v-if="form.isFullscreen === 0">
            <div class="preview-scrolling-container">
              <div class="preview-scrolling-text">{{ form.welcomeText || '这是欢迎语预览效果' }}</div>
              <div class="preview-scrolling-text preview-scrolling-text-duplicate">{{ form.welcomeText || '这是欢迎语预览效果' }}</div>
            </div>
          </template>
          <!-- 全屏模式：自动换行显示 -->
          <template v-else>
            <div class="preview-fullscreen-text">{{ form.welcomeText || '这是欢迎语预览效果' }}</div>
          </template>
        </div>
      </el-form-item>
      
      <el-form-item label="是否启用">
        <el-switch v-model="form.isEnabled"></el-switch>
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import axios from 'axios';
import config from '@/config';
import { parseApiResponse, handleApiError, camelToSnake, snakeToCamel } from '@/utils/apiResponse';

export default {
  name: 'WelcomeConfigForm',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    formData: {
      type: Object,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      form: {
        storeId: '',
        storeName: '',
        welcomeText: '',
        fontSize: 16,
        textColor: '#000000',
        backgroundColor: '',
        isEnabled: false,
        isFullscreen: 0 // 0=半屏，1=全屏
      },
      predefineColors: [
        '#ff4500',
        '#ff8c00',
        '#ffd700',
        '#90ee90',
        '#00ced1',
        '#1e90ff',
        '#c71585',
        '#000000',
        '#ffffff',
        'rgba(255, 69, 0, 0.68)',
        'rgb(255, 120, 0)',
        'hsv(51, 100, 98)',
        'hsva(120, 40, 94, 0.5)',
        'hsl(181, 100%, 37%)',
        'hsla(209, 100%, 56%, 0.73)',
        '#c7158577'
      ]
    };
  },
  computed: {
    previewStyle() {
      const baseStyle = {
        fontSize: this.form.fontSize + 'px',
        color: this.form.textColor,
        backgroundColor: this.form.backgroundColor || 'transparent',
        border: '1px solid #ddd',
        borderRadius: '4px',
        minHeight: Math.max(60, (parseInt(this.form.fontSize) || 16) * 2) + 'px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
      };
      
      // 全屏模式需要不同的 padding 和 overflow 处理
      if (this.form.isFullscreen === 1) {
        baseStyle.padding = '15px';
        baseStyle.overflow = 'visible';
        baseStyle.alignItems = 'flex-start';
      } else {
        baseStyle.padding = '10px';
        baseStyle.overflow = 'hidden';
      }
      
      return baseStyle;
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.initForm();
      }
    },
    dialogVisible(newVal) {
      if (!newVal) {
        this.$emit('update:visible', false);
      }
    },
    formData: {
      handler(newVal) {
        if (newVal) {
          this.initForm();
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    initForm() {
      if (this.formData) {
        this.form = {
          storeId: this.formData.storeId || '',
          storeName: this.formData.storeName || '',
          welcomeText: this.formData.welcomeText || '',
          fontSize: typeof this.formData.fontSize === 'number' ? this.formData.fontSize : parseInt(this.formData.fontSize) || 16,
          textColor: this.formData.textColor || this.formData.fontColor || this.formData.font_color || '#000000',
          backgroundColor: this.formData.backgroundColor || '',
          isEnabled: this.formData.isEnabled === 1 || this.formData.isEnabled === true,
          isFullscreen: this.formData.isFullscreen !== undefined ? this.formData.isFullscreen : 0
        };
      } else {
        this.resetForm();
      }
    },
    resetForm() {
      this.form = {
        storeId: '',
        storeName: '',
        welcomeText: '',
        fontSize: 16,
        textColor: '#000000',
        backgroundColor: '',
        isEnabled: false,
        isFullscreen: 0
      };
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate();
      }
    },
    handleClose() {
      this.dialogVisible = false;
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    handleSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (valid) {
          this.$emit('submit', { ...this.form });
          this.handleClose();
        } else {
          this.$message.error('请填写完整的表单信息');
          return false;
        }
      });
    }
  }
};
</script>

<style scoped>
/* 弹窗加宽：桌面端更宽；小屏自动占满大部分宽度，避免内容拥挤 */
:deep(.welcome-config-dialog) {
  max-width: 92vw;
}

@media (max-width: 768px) {
  :deep(.welcome-config-dialog) {
    width: 96vw !important;
    margin: 0 auto;
  }
}

.style-preview {
  min-height: 60px;
  border: 2px dashed #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px 0;
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

/* 半屏模式：滚动容器 */
.preview-scrolling-container {
  display: flex;
  width: max-content;
  animation: previewSeamlessScroll 20s linear infinite;
}

.preview-scrolling-text {
  white-space: nowrap;
  display: inline-block;
  flex-shrink: 0;
  padding-right: 4em;
}

/* 全屏模式：自动换行 */
.style-preview.preview-fullscreen {
  overflow: visible;
  align-items: flex-start;
  padding: 15px;
}

.preview-fullscreen-text {
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
  overflow-wrap: break-word;
  width: 100%;
  text-align: center;
}

/* 预览滚动动画 */
@keyframes previewSeamlessScroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
